export function isClassInSession() {
  return localStorage.getItem('gwa_class_in_session') === 'true';
}

export function formatDateRange(startDateStr, endDateStr) {
  if (!startDateStr && !endDateStr) return 'Date to be announced';

  const parseParts = (val) => {
    if (!val) return null;
    if (typeof val === 'string' && val.includes('-')) {
      const parts = val.split('T')[0].split('-').map(Number);
      if (parts.length === 3 && !parts.some(isNaN)) {
        return new Date(parts[0], parts[1] - 1, parts[2]);
      }
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };

  const start = parseParts(startDateStr);
  const end = parseParts(endDateStr);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (start && end) {
    const sMonth = monthNames[start.getMonth()];
    const eMonth = monthNames[end.getMonth()];
    const sDay = start.getDate();
    const eDay = end.getDate();
    const sYear = start.getFullYear();
    const eYear = end.getFullYear();

    if (sYear === eYear && sMonth === eMonth) {
      if (sDay === eDay) {
        return `${sMonth} ${sDay}, ${sYear}`;
      }
      return `${sMonth} ${sDay}–${eDay}, ${sYear}`;
    } else if (sYear === eYear) {
      return `${sMonth} ${sDay} – ${eMonth} ${eDay}, ${sYear}`;
    } else {
      return `${sMonth} ${sDay}, ${sYear} – ${eMonth} ${eDay}, ${sYear}`;
    }
  }

  if (start) {
    return `${monthNames[start.getMonth()]} ${start.getDate()}, ${start.getFullYear()}`;
  }

  if (end) {
    return `Through ${monthNames[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
  }

  return 'Date to be announced';
}

export function getCampStatus(user, settings) {
  if (!user) return { isEvergreen: false, isAAP: false, currentTrailDay: 0, windowOpen: false, isPostCamp: false };
  
  const passType = String(user.pass_type || 'free').trim().toLowerCase();
  const isEvergreen = passType === 'evergreen' || passType === 'evergreen-aap';
  const isAAP = passType === 'allaccess';
  const hasOngoingPassAccess = [
    'free',
    'paid',
    'allaccess',
    'evergreen',
    'evergreen-aap'
  ].includes(passType);
  const classInSession = isClassInSession();
  
  let currentTrailDay = 0;
  let windowOpen = false;

  // Window length from settings, default 14
  const windowLength = settings?.evergreen_replay_days ? parseInt(settings.evergreen_replay_days, 10) : 14;
  const now = new Date();

  // ── Helper: compute tomorrow's YYYY-MM-DD in local timezone ──
  // trail_start_date = the day AFTER signup. Day 1 unlocks at 7 AM
  // local time on trail_start_date. Each subsequent day (2–5) unlocks
  // at 7 AM on trail_start_date + (N-1) days.
  // Signup day is "prep day" — currentTrailDay = 0, all Cabin days locked.

  if (isEvergreen) {
    if (user.trail_start_date) {
      const [y, m, d] = user.trail_start_date.split('-');
      // 7 AM local time on trail_start_date = Day 1 unlock moment
      const day1Unlock = new Date(y, m - 1, d, 7, 0, 0, 0);
      
      const diffMs = now - day1Unlock;
      
      if (diffMs < 0) {
        // Before 7 AM on trail_start_date — nothing unlocked yet (prep day)
        currentTrailDay = 0;
      } else {
        // How many full 24-hour periods have elapsed since 7 AM on trail_start_date?
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        // Day 1 = 0 elapsed days, Day 2 = 1 elapsed day, etc.
        currentTrailDay = Math.min(diffDays + 1, 5);
      }

      if (hasOngoingPassAccess) {
        windowOpen = true;
      } else {
        // Window opens on signup day (trail_start_date - 1) and stays
        // open through windowLength days after trail_start_date.
        // diffDaysFromStart = -1 on signup day, 0 on trail_start_date, etc.
        const diffDaysFromStart = Math.floor((now - day1Unlock) / (1000 * 60 * 60 * 24));
        windowOpen = diffDaysFromStart >= -1 && diffDaysFromStart <= windowLength;
      }
    } else {
      // No trail_start_date — trail hasn't started yet.
      // Evergreen users can still see the app; all Cabin days are locked.
      currentTrailDay = 0;
      windowOpen = true;
    }
  } else {
    // Standard (live) camp logic — 7:00 AM Eastern Time on camp start date
    if (settings?.camp_start) {
      const start = new Date(`${settings.camp_start}T07:00:00-04:00`);
      const diffTime = now - start;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
      currentTrailDay = now >= start ? Math.max(diffDays, 1) : 0;
    }

    if (hasOngoingPassAccess) {
      windowOpen = true;
    } else {
      const replayExpiryStr = settings?.replay_expiry;
      windowOpen = replayExpiryStr ? now <= new Date(replayExpiryStr) : true;
    }
  }

  if (classInSession) {
    currentTrailDay = 5;
    windowOpen = true;
  }

  return {
    isEvergreen,
    isAAP,
    currentTrailDay,
    isPostCamp: !classInSession && currentTrailDay > 5,
    windowOpen,
    windowLength,
    classInSession
  };
}

/**
 * Compute trail_start_date = the day AFTER today (signup day) in the
 * camper's local timezone, returned as a YYYY-MM-DD string.
 *
 * Signup day = "prep day" (Day 0). The camper can explore the app but
 * all Cabin Call days are locked. Day 1 unlocks at 7 AM local time on
 * trail_start_date (the next morning).
 */
export function computeTrailStartDate() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const y = tomorrow.getFullYear();
  const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const d = String(tomorrow.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}